/**
 * Apply shared device-sync records into the local trip cache and stamp overlays.
 */

import { applyDeviceSyncRecords } from './deviceSync';
import type { DeviceSyncRecord } from './deviceSyncService';
import {
  applyOverlaysToTripDraft,
  parseCanonicalTripId,
  type DeviceSyncChangeRecord,
} from './sharedTripSyncService';
import { getUserIdForTripWrites, getAllTrips, updateTrip } from './tripsService';

export function toDeviceSyncRecords(records: DeviceSyncChangeRecord[]): DeviceSyncRecord[] {
  return records.map((r) => ({
    id: r.id,
    type: r.type,
    body: r.body ?? {},
    updatedAt: r.updatedAt ?? undefined,
    userId: parseCanonicalTripId(
      typeof r.body?.canonicalTripId === 'string' ? r.body.canonicalTripId : ''
    )?.authorSub,
  }));
}

export function mergeSharedTripRecordsIntoLocalStore(
  canonicalTripId: string,
  records: DeviceSyncChangeRecord[]
): void {
  const userId = getUserIdForTripWrites();
  if (!userId) return;

  const syncRecords = toDeviceSyncRecords(records);
  applyDeviceSyncRecords(syncRecords);

  const parsed = parseCanonicalTripId(canonicalTripId);
  if (!parsed) return;

  const trips = getAllTrips(userId);
  const trip = trips.find((t) => t.id.toLowerCase() === parsed.tripId.toLowerCase());
  if (!trip) return;

  const drafted = applyOverlaysToTripDraft(
    {
      ...trip,
      ownerId: trip.ownerId ?? parsed.authorSub,
    } as Record<string, unknown>,
    records,
    canonicalTripId
  );

  updateTrip(userId, trip.id, {
    ownerId: (drafted.ownerId as string) ?? parsed.authorSub,
    canEdit: drafted.canEdit as boolean | undefined,
    canonicalTripId: (drafted.canonicalTripId as string) ?? canonicalTripId,
    accessRole: (drafted.accessRole as string) ?? undefined,
  });
}
